import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Building2, Plus, Search, ShieldAlert } from 'lucide-react';
import { listVencendo } from '../api/certificado';
import { listEmitentesAll } from '../api/emitente';
import LoadingSpinner from '../components/LoadingSpinner';
import { useEmitenteAtivo } from '../context/EmitenteAtivoContext';
import { formatCNPJ, formatDate } from '../utils/formatters';

export default function DashboardPage() {
  const [busca, setBusca] = useState('');
  const [incluirInativos, setIncluirInativos] = useState(false);
  const { setEmitenteId } = useEmitenteAtivo();

  const empresasQ = useQuery({
    queryKey: ['emitentes', { all: true }],
    queryFn: listEmitentesAll
  });
  const vencQ = useQuery({
    queryKey: ['certs-vencendo'],
    queryFn: listVencendo
  });

  const stats = useMemo(() => {
    const lista = empresasQ.data ?? [];
    const ativas = lista.filter((e) => e.ativo !== false).length;
    const inativas = lista.length - ativas;
    const homologacao = lista.filter((e) => e.ambiente !== 'producao').length;
    return { ativas, inativas, homologacao, total: lista.length };
  }, [empresasQ.data]);

  const filtradas = useMemo(() => {
    const lista = empresasQ.data ?? [];
    const base = incluirInativos ? lista : lista.filter((e) => e.ativo !== false);
    const q = busca.trim().toLowerCase();
    if (!q) return base;
    // CNPJ alfanumerico: normalizar ambos para upper antes de comparar.
    const qUpper = q.toUpperCase();
    return base.filter((e) => {
      const nome = (e.nome_fantasia || e.razao_social || '').toUpperCase();
      const cnpj = (e.cnpj || '').toUpperCase();
      const municipio = (e.xmun || '').toUpperCase();
      return nome.includes(qUpper) || cnpj.includes(qUpper) || municipio.includes(qUpper);
    });
  }, [empresasQ.data, busca, incluirInativos]);

  const alertas = vencQ.data ?? [];

  return (
    <div className="animate-fade-in space-y-5">
      {alertas.length > 0 && (
        <section className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-500/25 dark:bg-amber-500/5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-amber-800 dark:text-amber-300">
            <span className="inline-flex items-center gap-1.5 font-semibold">
              <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
              Certificados:
            </span>
            {alertas.map((c) => (
              <Link
                key={c.emitente_id}
                to={`/empresa/${c.emitente_id}/editar`}
                className="underline-offset-2 hover:underline"
                title={`${formatCNPJ(c.cnpj)} · válido até ${formatDate(c.data_validade)}`}
              >
                {c.razao_social}{' '}
                {c.situacao === 'vencido' ? (
                  <span className="font-bold text-rose-700 dark:text-rose-400">(vencido)</span>
                ) : (
                  <span>({c.dias_restantes}d)</span>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white/90 shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none">
        <div className="flex flex-col gap-4 border-b border-slate-200 p-4 md:flex-row md:items-end md:justify-between md:p-5 dark:border-slate-800">
          <div className="min-w-0">
            <div className="eyebrow">Início</div>
            <h1 className="h-display mt-1 text-2xl text-slate-950 md:text-3xl dark:text-slate-100">Empresas</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-400">
              Selecione um emitente para emitir documentos, acompanhar eventos e corrigir rejeições.
            </p>
          </div>
          <Link to="/emitentes/novo" className="btn-primary min-h-10 text-sm">
            <Plus className="h-4 w-4" />
            Nova empresa
          </Link>
        </div>

        <div className="grid border-b border-slate-200 dark:border-slate-800 md:grid-cols-4">
          <div className="border-b border-slate-200 p-4 dark:border-slate-800 md:border-b-0 md:border-r">
            <div className="mono text-lg text-slate-950 dark:text-slate-100">{stats.total}</div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Total</div>
          </div>
          <div className="border-b border-slate-200 p-4 dark:border-slate-800 md:border-b-0 md:border-r">
            <div className="mono text-lg text-emerald-700 dark:text-emerald-300">{stats.ativas}</div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Ativas</div>
          </div>
          <div className="border-b border-slate-200 p-4 dark:border-slate-800 md:border-b-0 md:border-r">
            <div className="mono text-lg text-amber-700 dark:text-amber-300">{stats.homologacao}</div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Homologação</div>
          </div>
          <div className="p-4">
            <div className={`mono text-lg ${alertas.length ? 'text-amber-700 dark:text-amber-300' : 'text-slate-600 dark:text-slate-300'}`}>{alertas.length}</div>
            <div className="mt-1 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Certificados em alerta</div>
          </div>
        </div>

        <div className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:p-5">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome, CNPJ ou município"
              className="field-input min-h-11 pl-9"
            />
          </div>
          <label className="inline-flex min-h-11 select-none items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
            <input
              type="checkbox"
              checked={incluirInativos}
              onChange={(e) => setIncluirInativos(e.target.checked)}
              className="rounded border-slate-300 bg-white text-emerald-600 focus:ring-emerald-500/30 dark:border-slate-700 dark:bg-slate-950 dark:text-emerald-500"
            />
            Mostrar inativas
          </label>
        </div>
      </section>

      {empresasQ.isLoading && (
        <div className="grid place-items-center py-12">
          <LoadingSpinner label="Carregando empresas..." />
        </div>
      )}

      {!empresasQ.isLoading && filtradas.length === 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white/90 p-8 text-center shadow-sm dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none">
          <Building2 className="mx-auto h-8 w-8 text-slate-500" />
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            {busca ? `Nenhuma empresa para "${busca}".` : 'Nenhuma empresa cadastrada ainda.'}
          </p>
          {!busca && (
            <Link to="/emitentes/novo" className="btn-primary mt-4 inline-flex text-sm">
              <Plus className="h-4 w-4" />
              Cadastrar primeira empresa
            </Link>
          )}
        </div>
      )}

      {!empresasQ.isLoading && filtradas.length > 0 && (
        <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
          {filtradas.map((e) => {
            const ativa = e.ativo !== false;
            const nome = e.nome_fantasia || e.razao_social;
            const cidadeUf = [e.xmun, e.uf].filter(Boolean).join(' - ');
            const ambiente = e.ambiente === 'producao' ? 'producao' : 'homologacao';
            return (
              <li key={e.id}>
                <Link
                  to={`/empresa/${e.id}`}
                  onClick={() => setEmitenteId(e.id)}
                  className="group flex min-h-[78px] items-center gap-3 rounded-xl border border-slate-200 bg-white/90 p-3 shadow-sm transition hover:border-emerald-500/35 hover:bg-slate-50 active:scale-[0.99] dark:border-slate-800 dark:bg-slate-950/70 dark:shadow-none dark:hover:bg-slate-900/70"
                >
                  <div className={`grid h-10 w-10 shrink-0 place-items-center rounded-lg border ${ativa ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-500/25 dark:bg-emerald-500/10' : 'border-slate-200 bg-slate-100 dark:border-slate-700 dark:bg-slate-800/60'}`}>
                    <Building2 className={`h-4 w-4 ${ativa ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-500'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <h3 className="truncate text-sm font-semibold leading-tight text-slate-950 dark:text-slate-100" title={nome}>{nome}</h3>
                      {!ativa && <span className="badge-neutral shrink-0">Inativa</span>}
                    </div>
                    <div className="mono mt-1 truncate text-[11px] text-slate-600 dark:text-slate-400">
                      {formatCNPJ(e.cnpj)}{cidadeUf && ` · ${cidadeUf}`}
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      {ambiente === 'producao' ? <span className="badge-success">Produção</span> : <span className="badge-warning">Homologação</span>}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-slate-600 transition group-hover:translate-x-0.5 group-hover:text-emerald-700 dark:group-hover:text-emerald-400" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
